import React from 'react';
import { CommonUtils } from './commonUtils';
import { getUserInfo } from './auth';

// 공통 타입 정의
export interface FormData {
  userId: string;
  password: string;
  confirmPassword: string;
  status: string;
  name: string;
  mfaType: string;
  department: string;
  phoneNumber: string;
}

export interface FormErrors {
  userId?: string;
  password?: string;
  confirmPassword?: string;
  status?: string;
  name?: string;
  mfaType?: string;
  department?: string;
  phoneNumber?: string;
}

// 공통 유틸리티 함수들
export class UserFormUtils {
  // 폼 검증 함수 (기본)
  static validateForm(formData: FormData): FormErrors {
    return this.validateCreateForm(formData);
  }

  // 사용자 생성 폼 검증
  static validateCreateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.userId) {
      newErrors.userId = '아이디는 필수입니다';
    } else if (formData.userId.length > 20) {
      newErrors.userId = '아이디는 20자 이하여야 합니다';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.userId)) {
      newErrors.userId = '아이디는 영문, 숫자, 하이픈(-), 언더스코어(_)만 사용 가능합니다';
    }
    
    if (!formData.password) {
      newErrors.password = '비밀번호는 필수입니다';
    } else if (formData.password.length < 8) {
      newErrors.password = '비밀번호는 8자 이상이어야 합니다';
    } else if (formData.password.length > 20) {
      newErrors.password = '비밀번호는 20자 이하여야 합니다';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
    }
    
    if (!formData.name) {
      newErrors.name = '사용자명은 필수입니다';
    } else if (formData.name.length > 50) {
      newErrors.name = '사용자명은 50자 이하여야 합니다';
    }
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = '연락처는 필수입니다';
    } else if (!/^[0-9-]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = '올바른 전화번호 형식이 아닙니다';
    } else if (formData.phoneNumber.length > 20) {
      newErrors.phoneNumber = '연락처는 20자 이하여야 합니다';
    }
    
    return newErrors;
  }

  // 사용자 수정 폼 검증
  static validateUpdateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.userId) {
      newErrors.userId = '아이디는 필수입니다';
    } else if (formData.userId.length > 20) {
      newErrors.userId = '아이디는 20자 이하여야 합니다';
    }
    
    // 비밀번호는 수정 시 선택적 (입력한 경우에만 검증)
    if (formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = '비밀번호는 8자 이상이어야 합니다';
      } else if (formData.password.length > 20) {
        newErrors.password = '비밀번호는 20자 이하여야 합니다';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
      }
    }
    
    if (!formData.name) {
      newErrors.name = '사용자명은 필수입니다';
    } else if (formData.name.length > 50) {
      newErrors.name = '사용자명은 50자 이하여야 합니다';
    }
    
    if (formData.phoneNumber && !/^[0-9-]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = '올바른 전화번호 형식이 아닙니다';
    } else if (formData.phoneNumber && formData.phoneNumber.length > 20) {
      newErrors.phoneNumber = '연락처는 20자 이하여야 합니다';
    }
    
    return newErrors;
  }

  // 입력값 변경 처리 함수
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  }

  // 사용자 상태 배지 생성 (다른 도메인과 동일하게 객체 반환)
  static getStatusBadge(status: string) {
    // CommonUtils에는 'user' 타입이 없으므로 'product' 타입 사용
    return CommonUtils.getStatusBadge(status, 'product');
  }

  // 날짜 포맷팅
  static formatDate(dateString: string): string {
    return CommonUtils.formatDate(dateString);
  }

  // 사용자 정보 로드
  static loadUserInfo(): any {
    return getUserInfo();
  }

  // 기본 폼 데이터
  static getDefaultFormData(): FormData {
    return {
      userId: '',
      password: '',
      confirmPassword: '',
      status: 'ACTIVE',
      name: '',
      mfaType: 'NONE',
      department: '',
      phoneNumber: ''
    };
  }

  // 모바일 사이드바 리사이즈 핸들러
  static useMobileSidebarResize(
    setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    return CommonUtils.useMobileSidebarResize(setIsMobileSidebarOpen);
  }

  // 체크박스 핸들러
  static handleSelectAll(
    selectedUsers: string[],
    filteredUsers: any[],
    setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectAll(selectedUsers, filteredUsers, setSelectedUsers, (user) => user.userId);
  }

  static handleSelectUser(
    userId: string,
    selectedUsers: string[],
    setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectItem(userId, selectedUsers, setSelectedUsers);
  }

  // 페이지네이션 핸들러
  static handlePageChange(
    page: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageChange(page, setCurrentPage);
  }

  static handlePageSizeChange(
    size: number,
    setPageSize: React.Dispatch<React.SetStateAction<number>>,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  }

  // 정렬 핸들러
  static handleSort(
    field: string,
    currentSortField: string,
    currentSortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSort(field, currentSortField, currentSortOrder, setSortField, setSortOrder);
  }

  // 검색 및 필터 핸들러
  static handleSearch(
    searchTerm: string,
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSearch(searchTerm, setSearchTerm);
  }

  static handleStatusFilter(
    status: string,
    setSelectedStatus: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleStatusFilter(status, setSelectedStatus);
  }

  // 사용자 생성 페이지로 이동
  static handleUserCreate(navigate: (path: string) => void) {
    navigate('/users/create');
  }

  // 사용자 수정 페이지로 이동
  static handleUserEdit(navigate: (path: string) => void, userId: string, currentPage: number, pageSize: number) {
    navigate(`/users/${userId}/update?page=${currentPage}&size=${pageSize}`);
  }

  // 사용자 삭제 처리
  static handleUserDelete(userId: string, onDelete?: (id: string) => void) {
    return CommonUtils.handleDelete(userId, '사용자', onDelete);
  }

  // 폼 제출 처리 (사용자 생성)
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getAccountCode: () => string | null,
    createUser: (accountCode: string, data: any) => Promise<any>,
    navigate: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const accountCode = getAccountCode();
      if (!accountCode) {
        throw new Error('계정 정보를 찾을 수 없습니다.');
      }

      const userData = {
        userId: formData.userId,
        password: formData.password,
        status: formData.status,
        name: formData.name,
        mfaType: formData.mfaType || 'NONE',
        department: formData.department || undefined,
        phoneNumber: formData.phoneNumber || undefined
      };

      const result = await createUser(accountCode, userData);
      
      if (result.success) {
        alert('사용자가 성공적으로 생성되었습니다.');
        navigate('/users');
      } else {
        setSubmitError(result.message || '사용자 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('사용자 생성 오류:', error);
      const displayMessage = CommonUtils.getNetworkErrorMessage(error);
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 폼 제출 처리 (사용자 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getAccountCode: () => string | null,
    userId: string,
    updateUser: (accountCode: string, userId: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const accountCode = getAccountCode();
      if (!accountCode) {
        throw new Error('계정 정보를 찾을 수 없습니다.');
      }

      const userData: any = {
        userId: formData.userId,
        status: formData.status,
        name: formData.name,
        mfaType: formData.mfaType || 'NONE',
        department: formData.department || undefined,
        phoneNumber: formData.phoneNumber || undefined
      };

      // 비밀번호가 입력된 경우에만 포함
      if (formData.password) {
        userData.password = formData.password;
      }

      const result = await updateUser(accountCode, userId, userData);
      
      if (result.success) {
        setHasChanges(false);
        setOriginalData({...formData});
        // 사용자 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/users?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '사용자 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('사용자 수정 오류:', error);
      const displayMessage = CommonUtils.getNetworkErrorMessage(error);
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }
}